import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, Store } from 'lucide-react';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminStores() {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    codigo: '',
    direccion: '',
    telefono: ''
  });

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data);
    } catch (error) {
      toast.error('Error al cargar tiendas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingStore) {
        await axios.put(`${API}/admin/stores/${editingStore.id}`, formData);
        toast.success('Tienda actualizada');
      } else {
        await axios.post(`${API}/admin/stores`, formData);
        toast.success('Tienda creada');
      }
      
      setShowDialog(false);
      setEditingStore(null);
      setFormData({ nombre: '', codigo: '', direccion: '', telefono: '' });
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar tienda');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      nombre: store.nombre,
      codigo: store.codigo || '',
      direccion: store.direccion || '',
      telefono: store.telefono || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta tienda?')) return;

    try {
      await axios.delete(`${API}/admin/stores/${storeId}`);
      toast.success('Tienda eliminada');
      fetchStores();
    } catch (error) {
      toast.error('Error al eliminar tienda');
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setTimeout(() => {
      setEditingStore(null);
      setFormData({ nombre: '', codigo: '', direccion: '', telefono: '' });
    }, 300);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-3xl font-bold text-slate-900">Gestión de Tiendas</h1>
          </div>
          <Button onClick={() => setShowDialog(true)} className="bg-[#2c57c5] hover:bg-[#1e3a8a]">
            <Plus className="h-4 w-4 mr-2" />
            Nueva Tienda
          </Button>
        </div>

        {/* Stores List */}
        <div className="grid gap-4">
          {stores.length === 0 ? (
            <Card className="p-8 text-center">
              <Store className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No hay tiendas registradas</p>
              <Button 
                onClick={() => setShowDialog(true)}
                variant="outline" 
                className="mt-4"
              >
                Crear primera tienda
              </Button>
            </Card>
          ) : (
            stores.map((store) => (
              <Card key={store.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Store className="h-5 w-5 text-[#2c57c5]" />
                      <h3 className="text-lg font-semibold text-slate-900">{store.nombre}</h3>
                      {store.codigo && (
                        <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded">
                          {store.codigo}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-600 space-y-1 ml-8">
                      {store.direccion && <p>📍 {store.direccion}</p>}
                      {store.telefono && <p>📞 {store.telefono}</p>}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEdit(store)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(store.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>
                  {editingStore ? 'Editar Tienda' : 'Crear Nueva Tienda'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="nombre">Nombre *</Label>
                  <Input
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                    required
                    placeholder="Nombre de la tienda"
                  />
                </div>
                <div>
                  <Label htmlFor="codigo">Código</Label>
                  <Input
                    id="codigo"
                    value={formData.codigo}
                    onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                    placeholder="Código interno (opcional)"
                  />
                </div>
                <div>
                  <Label htmlFor="direccion">Dirección</Label>
                  <Input
                    id="direccion"
                    value={formData.direccion}
                    onChange={(e) => setFormData({...formData, direccion: e.target.value})}
                    placeholder="Dirección completa (opcional)"
                  />
                </div>
                <div>
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    placeholder="Teléfono de contacto (opcional)"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseDialog}
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="flex-1 bg-[#2c57c5] hover:bg-[#1e3a8a]"
                  >
                    {loading ? 'Guardando...' : (editingStore ? 'Actualizar' : 'Crear')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}
